import { createId, roundMoney } from '@/engines/budget'

import { commercialCatalog, type CommercialCatalog } from '../catalog'
import type { CommercialItem, CommercialItemType } from '../types'

const SIMPLE_TYPES: CommercialItemType[] = [
  'Notebook',
  'Printer',
  'Monitor',
  'Peripheral',
  'Software',
  'Service',
  'License',
]

/**
 * Builds a flat CommercialItem from a catalog finished-good entry.
 * Does not apply to Computer (use ComputerBuilder).
 */
export class SimpleItemBuilder {
  private readonly catalog: CommercialCatalog

  constructor(catalog: CommercialCatalog = commercialCatalog) {
    this.catalog = catalog
  }

  fromCatalog(
    catalogId: string,
    options?: { quantity?: number; name?: string; templateId?: string },
  ): CommercialItem {
    const entry = this.catalog.getById(catalogId)
    if (!entry) {
      throw Object.assign(new Error(`Item não encontrado: ${catalogId}`), {
        code: 'CATALOG_NOT_FOUND',
      })
    }
    if (entry.type === 'Component' || entry.type === 'Computer') {
      throw Object.assign(
        new Error('Use ComputerBuilder para montar um Computador a partir de componentes'),
        { code: 'USE_COMPUTER_BUILDER' },
      )
    }
    if (!SIMPLE_TYPES.includes(entry.type)) {
      throw Object.assign(new Error(`Tipo comercial não suportado: ${entry.type}`), {
        code: 'UNSUPPORTED_TYPE',
      })
    }

    const quantity = Math.max(1, Math.floor(options?.quantity ?? 1))
    return {
      id: createId(),
      type: entry.type,
      name: options?.name ?? entry.label,
      description: entry.description,
      quantity,
      unitCost: roundMoney(entry.unitCost),
      unitPrice: roundMoney(entry.unitPrice),
      origin: {
        source: options?.templateId ? 'template' : 'catalog',
        catalogId: entry.id,
        templateId: options?.templateId,
      },
      meta: {
        commercialType: entry.type,
        brand: entry.brand,
        model: entry.model,
      },
    }
  }
}

export const simpleItemBuilder = new SimpleItemBuilder()
